import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DateSelector from "@portal/shared/DateSelector";
import WarehouseSelector from "@portal/components/WarehouseSelector";
import { getAPIDataParamsReturn } from "@portal/utils/apiFunction";
import { startOfWeekMon, endOfWeekSun, shiftHours, textfieldDateFormat, dateFormat } from "@portal/utils/dateFunctions";
import { numberFormat } from "@portal/utils/numberFunctions";
import { shiftWeek } from "app/utils/dateFunctions";

const ProcurementDashboard = () => {
    const noWeeks = 1;
    const [startDate, setStartDate] = React.useState(shiftHours(startOfWeekMon(new Date()), -7 * 24));
    const [endDate, setEndDate] = React.useState(shiftHours(endOfWeekSun(new Date()), (noWeeks - 1) * 7 * 24));
    const [location, setLocation] = React.useState(0);
    const [usePromisedDate, setUsePromisedDate] = React.useState(false);
    const [supplierFilter, setSupplierFilter] = React.useState("");
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");

    React.useEffect(() => {
        const abortController = new AbortController();
        setLoading(true);
        setError("");
        getAPIDataParamsReturn(
            "/PurchaseOrderSummary/Get",
            {
                StartDate: textfieldDateFormat(startDate),
                EndDate: textfieldDateFormat(endDate),
                Location: location || undefined,
                UsePromisedDate: usePromisedDate,
            },
            abortController
        )
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                const filtered = list.filter((x) => (x.status != 'Canceled') && (x.status != 'Cancelled'));
                setRows(filtered);
            })
            .catch(() => {
                setError("Unable to load purchase order summaries.");
                setRows([]);
            })
            .finally(() => setLoading(false));

        return () => abortController.abort();
    }, [startDate, endDate, location, usePromisedDate]);

    const filteredRows = React.useMemo(() => {
        const term = supplierFilter.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter((row) =>
            [row.supplierName, row.supplierIdText]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(term))
        );
    }, [rows, supplierFilter]);

    const allGrades = React.useMemo(() => {
        const set = new Set();
        rows.forEach((row) => {
            try {
                const parsed = JSON.parse(row.linesJson || "[]");
                parsed.forEach((l) => {
                    if (l.StockItemDescription) set.add(l.StockItemDescription);
                });
            } catch (e) {
                /* ignore bad json */
            }
        });
        return Array.from(set);
    }, [rows]);

    const getLineSummaries = (row) => {
        const map = {};
        try {
            const parsed = JSON.parse(row.linesJson || "[]");
            parsed.forEach((l) => {
                const key = l.StockItemDescription || "";
                if (!key) return;
                if (!map[key]) {
                    map[key] = { qty: 0, price: l.Price };
                }
                map[key].qty += l.Ordered || 0;
                // keep first seen price
                if (map[key].price === undefined && l.Price !== undefined) {
                    map[key].price = l.Price;
                }
            });
        } catch (e) {
            return map;
        }
        return map;
    };
    const formatSpecies = (text) => {
        if (!text) return text;
        return text
            .replace(/sydney rock/gi, "SR")
            .replace(/pacific/gi, "PC")
            .replace(/angasi/gi, "AN");
    }
    const formatCurrency = (value) => {
        const num = typeof value === "number" ? value : parseFloat(value);
        if (Number.isFinite(num)) {
            return `$${numberFormat(num)}`;
        }
        return value ?? "";
    };

    const calcOrderTotal = (row) => {
        try {
            const parsed = JSON.parse(row.linesJson || "[]");
            return parsed.reduce((sum, l) => {
                const qty = Number(l.Ordered) || 0;
                const price = Number(l.Price) || 0;
                return sum + qty * price;
            }, 0);
        } catch (e) {
            return null;
        }
    };

    const totals = React.useMemo(() => {
        const gradeTotals = {};
        let orderTotal = 0;
        filteredRows.forEach((row) => {
            try {
                const parsed = JSON.parse(row.linesJson || "[]");
                parsed.forEach((l) => {
                    const key = l.StockItemDescription || "";
                    if (!key) return;
                    if (!gradeTotals[key]) gradeTotals[key] = 0;
                    gradeTotals[key] += Number(l.Ordered) || 0;
                    orderTotal += (Number(l.Ordered) || 0) * (Number(l.Price) || 0);
                });
            } catch (e) {
                /* ignore */
            }
        });
        return { gradeTotals, orderTotal };
    }, [filteredRows]);

    const rowSx = (status) => {
        const isComplete = status && ["completed", "closed"].includes(status.toLowerCase());
        return isComplete ? { backgroundColor: "#e6f4ea" } : {};
    };

    return (
        <div>
            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={4} lg={3}>
                    <DateSelector montosun startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}  noForward maxDate={shiftWeek(new Date(), 1).end} />
                </Grid>

            </Grid>

            <Card>
                <CardHeader title="AOC Orders Summary" />
                <CardContent>
                    {loading ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Loading…</Typography>
                        </Stack>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>PO #</TableCell>
                                        <TableCell>Supplier</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Order Date</TableCell>
                                        <TableCell>Warehouse</TableCell>
                                        {allGrades.map((g) => (
                                            <React.Fragment key={g}>
                                                <TableCell>{formatSpecies(g)} (doz)</TableCell>
                                                <TableCell align="right" style={{ borderRight: '1px solid black' }}>$/doz</TableCell>
                                            </React.Fragment>
                                        ))}
                                        <TableCell align="right">Order Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.map((row) => {
                                        const lineMap = getLineSummaries(row);
                                        return (
                                            <TableRow key={row.purchaseOrderId} sx={rowSx(row.status)}>
                                                <TableCell>{row.myobIdText || row.purchaseOrderId}</TableCell>
                                                <TableCell>{row.supplierName || row.supplierIdText}</TableCell>
                                                <TableCell>{row.status}</TableCell>
                                                <TableCell>{row.orderDateText ? dateFormat(new Date(row.orderDateText)) : ""}</TableCell>
                                                <TableCell>{row.warehouseName || row.warehouseIdText}</TableCell>
                                                {allGrades.map((g) => {
                                                    const entry = lineMap[g];
                                                    return (
                                                        <React.Fragment key={g}>
                                                            <TableCell>{entry ? numberFormat(entry.qty) : ""}</TableCell>
                                                            <TableCell align="right" style={{ borderRight: '1px solid black' }}>{entry ? formatCurrency(entry.price) : ""}</TableCell>
                                                        </React.Fragment>
                                                    );
                                                })}
                                                <TableCell align="right">{formatCurrency(calcOrderTotal(row))}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {filteredRows.length > 0 && (
                                        <TableRow sx={{ fontWeight: 1000 }}>
                                            <TableCell sx={{ fontWeight: 1000 }}>Totals</TableCell>
                                            <TableCell colSpan={4}></TableCell>
                                            {allGrades.map((g) => (
                                                <React.Fragment key={`total-${g}`}>
                                                    <TableCell sx={{ fontWeight: 1000 }}>{totals.gradeTotals[g] ? numberFormat(totals.gradeTotals[g]) : ""}</TableCell>
                                                    <TableCell align="right" style={{ borderRight: '1px solid black' }}></TableCell>
                                                </React.Fragment>
                                            ))}
                                            <TableCell align="right" sx={{ fontWeight: 1000 }}>{formatCurrency(totals.orderTotal)}</TableCell>
                                        </TableRow>
                                    )}
                                    {!loading && filteredRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5 + allGrades.length * 2}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {error || "No purchase orders found for this range."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ProcurementDashboard;

